(() => {
  const ROOT_SELECTOR = '.wa-home[data-section-id]';
  const DESKTOP_QUERY = '(min-width: 1101px) and (min-height: 620px)';
  const SPEED_RATIO = 0.78;
  const EASE = 0.105;
  const EPSILON = 0.18;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function initHome(root) {
    if (!root || root.dataset.waScrollInit === 'true') return;

    const scene = root.querySelector('[data-wa-scroll-scene]');
    const sticky = root.querySelector('[data-wa-sticky]');
    const track = root.querySelector('[data-wa-track]');
    const panels = Array.from(root.querySelectorAll('[data-wa-panel]'));
    const progressBar = root.querySelector('[data-wa-progress]');
    const count = root.querySelector('[data-wa-scroll-count]');
    if (!scene || !sticky || !track || !panels.length) return;

    root.dataset.waScrollInit = 'true';
    const controller = new AbortController();
    root._waScrollController = controller;
    const { signal } = controller;

    const desktop = window.matchMedia(DESKTOP_QUERY);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');


    const emailForm = root.querySelector('.wa-home__form');
    if (emailForm) {
      emailForm.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        if (emailForm.dataset.waSubmitting === 'true') return;
        if (!emailForm.checkValidity()) {
          emailForm.reportValidity();
          return;
        }

        const submitButton = emailForm.querySelector('[data-wa-home-form-submit]');
        let status = emailForm.querySelector('[data-wa-home-form-status]');
        if (!status) {
          status = document.createElement('div');
          status.className = 'wa-home__form-status';
          status.dataset.waHomeFormStatus = '';
          status.setAttribute('aria-live', 'polite');
          emailForm.appendChild(status);
        }

        emailForm.dataset.waSubmitting = 'true';
        emailForm.classList.add('is-submitting');
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.dataset.originalLabel = submitButton.innerHTML;
          submitButton.textContent = 'Joining...';
        }
        status.className = 'wa-home__form-status';
        status.setAttribute('role', 'status');
        status.textContent = '';

        /*
         * Shopify's customer form redirects after a successful marketing opt-in.
         * Posting into a hidden same-origin iframe lets Shopify complete its normal
         * customer/subscriber workflow without navigating the storefront away from
         * the horizontal homepage. It also avoids treating Shopify's redirect as an
         * AJAX failure after the customer has actually been saved.
         */
        const frameName = `wa-home-signup-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const frame = document.createElement('iframe');
        frame.name = frameName;
        frame.title = 'Email signup response';
        frame.setAttribute('aria-hidden', 'true');
        frame.tabIndex = -1;
        frame.style.position = 'absolute';
        frame.style.width = '1px';
        frame.style.height = '1px';
        frame.style.opacity = '0';
        frame.style.pointerEvents = 'none';
        frame.style.border = '0';
        frame.style.clipPath = 'inset(50%)';
        frame.style.overflow = 'hidden';

        let submitted = false;
        let settled = false;
        const finish = (success) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(fallbackTimer);

          if (success) {
            emailForm.classList.remove('is-submitting');
            emailForm.classList.add('is-success');
            emailForm.removeAttribute('target');
            emailForm.innerHTML = '<p class="wa-home__form-success" role="status">You\'re in. Product drops and updates will land here.</p>';
          } else {
            emailForm.dataset.waSubmitting = 'false';
            emailForm.classList.remove('is-submitting');
            emailForm.removeAttribute('target');
            if (submitButton) {
              submitButton.disabled = false;
              submitButton.innerHTML = submitButton.dataset.originalLabel || 'Join the list';
            }
            status.className = 'wa-home__form-errors';
            status.setAttribute('role', 'alert');
            status.textContent = 'We could not confirm that signup. Please try again.';
          }

          window.setTimeout(() => frame.remove(), 250);
        };

        frame.addEventListener('load', () => {
          if (!submitted || settled) return;

          let confirmed = true;
          try {
            const frameUrl = new URL(frame.contentWindow.location.href);
            const bodyText = (frame.contentDocument && frame.contentDocument.body)
              ? frame.contentDocument.body.textContent || ''
              : '';
            const explicitFailure = /\/challenge(?:\/|$)/i.test(frameUrl.pathname) || /captcha|verify you are human/i.test(bodyText);
            confirmed = !explicitFailure;
          } catch (_) {
            /* If Shopify changes the final response origin, a completed load still
               means the native POST was handed off successfully. */
            confirmed = true;
          }

          finish(confirmed);
        });

        document.body.appendChild(frame);
        emailForm.setAttribute('target', frameName);

        /* A native form post is deliberate here. Calling the prototype method
           bypasses other theme submit handlers while keeping Shopify's own form
           endpoint, hidden fields, and marketing-consent behavior intact. */
        submitted = true;
        HTMLFormElement.prototype.submit.call(emailForm);

        const fallbackTimer = window.setTimeout(() => {
          /* Do not flash a false failure after Shopify has accepted the address.
             If the iframe response is unusually slow, leave the button usable. */
          if (!settled) finish(false);
        }, 12000);
      }, { capture: true, signal });
    }

    let horizontal = false;
    let horizontalDistance = 0;
    let verticalDistance = 0;
    let sceneStart = 0;
    let currentX = 0;
    let targetX = 0;
    let animationFrame = 0;
    let resizeTimer;

    function setCount(index) {
      if (!count) return;
      const current = String(index + 1).padStart(2, '0');
      const total = String(panels.length).padStart(2, '0');
      count.textContent = `${current} / ${total}`;
    }

    function currentPanelIndex(translatedX) {
      const marker = translatedX + window.innerWidth * 0.5;
      let index = 0;
      panels.forEach((panel, i) => {
        if (marker >= panel.offsetLeft) index = i;
      });
      return clamp(index, 0, panels.length - 1);
    }

    function paint(x) {
      track.style.transform = `translate3d(${-x}px, 0, 0)`;
      const progress = horizontalDistance > 0 ? clamp(x / horizontalDistance, 0, 1) : 0;
      if (progressBar) progressBar.style.transform = `scaleX(${progress})`;
      setCount(currentPanelIndex(x));
    }

    function animate() {
      animationFrame = 0;
      if (!horizontal) return;

      const delta = targetX - currentX;
      if (Math.abs(delta) <= EPSILON) {
        currentX = targetX;
        paint(currentX);
        return;
      }

      // Exponential interpolation gives mouse wheels the same fluid feel as
      // a trackpad while still following native page scroll/accessibility.
      currentX += delta * EASE;
      paint(currentX);
      animationFrame = window.requestAnimationFrame(animate);
    }

    function requestAnimation() {
      if (!animationFrame) animationFrame = window.requestAnimationFrame(animate);
    }

    function syncTargetFromScroll(immediate = false) {
      if (!horizontal || verticalDistance <= 0) return;
      const progress = clamp((window.scrollY - sceneStart) / verticalDistance, 0, 1);
      targetX = progress * horizontalDistance;
      if (immediate) {
        currentX = targetX;
        paint(currentX);
      } else {
        requestAnimation();
      }
    }

    function clearHorizontal() {
      horizontal = false;
      root.classList.remove('wa-home--horizontal');
      root.style.removeProperty('--wa-sticky-top');
      root.style.removeProperty('--wa-panel-height');
      scene.style.height = '';
      track.style.transform = '';
      if (progressBar) progressBar.style.transform = '';
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      animationFrame = 0;
      currentX = 0;
      targetX = 0;
      setCount(0);
    }

    function layout() {
      const shouldUseHorizontal = desktop.matches && !reducedMotion.matches;
      if (!shouldUseHorizontal) {
        clearHorizontal();
        return;
      }

      horizontal = true;
      root.classList.add('wa-home--horizontal');

      const viewportHeight = Math.max(520, window.innerHeight);
      root.style.setProperty('--wa-sticky-top', '0px');
      root.style.setProperty('--wa-panel-height', `${viewportHeight}px`);

      track.style.transform = 'translate3d(0, 0, 0)';
      scene.style.height = `${viewportHeight}px`;

      window.requestAnimationFrame(() => {
        horizontalDistance = Math.max(0, track.scrollWidth - window.innerWidth);
        verticalDistance = Math.max(viewportHeight * 0.5, horizontalDistance * SPEED_RATIO);
        scene.style.height = `${viewportHeight + verticalDistance}px`;
        sceneStart = scene.getBoundingClientRect().top + window.scrollY;
        syncTargetFromScroll(true);

        if (window.location.hash) {
          const target = root.querySelector(window.location.hash);
          if (target) scrollToTarget(target, false);
        }
      });
    }

    function scrollToTarget(target, smooth = true) {
      const panel = target.closest('[data-wa-panel]') || target;
      if (!horizontal || horizontalDistance <= 0 || !panel) {
        target.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        return;
      }

      const targetPanelX = clamp(panel.offsetLeft, 0, horizontalDistance);
      const progress = horizontalDistance ? targetPanelX / horizontalDistance : 0;
      const top = sceneStart + progress * verticalDistance;
      window.scrollTo({ top, behavior: smooth ? 'smooth' : 'auto' });
    }

    root.addEventListener('click', (event) => {
      const link = event.target.closest('a[href^="#"]');
      if (!link) return;
      const hash = link.getAttribute('href');
      if (!hash || hash === '#') return;
      let target;
      try { target = root.querySelector(hash); } catch (_) { return; }
      if (!target) return;
      event.preventDefault();
      if (history.replaceState) history.replaceState(null, '', hash);
      scrollToTarget(target, true);
    }, { signal });

    window.addEventListener('scroll', () => syncTargetFromScroll(false), { passive: true, signal });
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(layout, 140);
    }, { passive: true, signal });

    const mediaChanged = () => layout();
    if (desktop.addEventListener) desktop.addEventListener('change', mediaChanged, { signal });
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', mediaChanged, { signal });

    layout();
  }

  function initAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initHome);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector?.(ROOT_SELECTOR);
    if (root?._waScrollController) root._waScrollController.abort();
  });
})();
